<script setup lang="ts">
import { useModel } from './../model/model'
import { VIcons, VModal } from '@/shared/ui'
import { computed, type PropType, ref } from 'vue'
import { useHelpers, useTranslate } from '@/shared/lib'
import { ObjectSearch } from '@/widgets'
import type { AdminConfiguratorEventsType } from '@/entities/Cabinet'
import { useConfiguratorTemplateSave } from './../model/api'
import { useRoute } from 'vue-router'
import { ObjectTypeIds } from '@/entities/Object'

const props = defineProps({
  events: {
    type: Array as PropType<AdminConfiguratorEventsType[]>,
    required: true,
  },
})

const model = useModel()
const { trs } = useTranslate()
const { numberList } = useHelpers()
const route = useRoute()

const numbers = numberList(63)

/**
 * Вычисляем значение заголовка
 */
const title = computed(() => trs(model.data.id ? 'itf.templates_edit' : 'itf.templates_create'))

/**
 * Вычисляем свободные номера входов
 */
const pinNumbers = computed(() => {
  const excludeNumbers = model.data.template.map((item) => item.pin)
  return numbers.filter((item) => !excludeNumbers.includes(item))
})

/**
 * Обработка события добавления строки
 */
const addHandler = () => {
  const index = model.data.template.length - 1 < 0 ? 0 : model.data.template.length
  model.addRow(pinNumbers.value[0], props.events[index] || props.events[3])
}

const valid = ref(false)

const { save, isPending, data } = useConfiguratorTemplateSave(Number(route.params.object_id))

/**
 * Функция отправки формы
 */
const submitHandler = async () => {
  await save(model.data, valid.value)

  if (data.value && !model.data.id) {
    model.data.id = data.value.data
  }
}
</script>

<template>
  <VModal v-model="model.openForm" :max-width="780">
    <template #title>
      {{ title }}
    </template>

    <VForm v-model="valid">
      <ObjectSearch v-model="model.data.owner_group_id" :object_type="ObjectTypeIds.GROUPS" />

      <VTextField :label="$t('itf.name')" v-model="model.data.name" :rules="model.rules.name" />

      <VDataTable
        :items-per-page="-1"
        hide-default-footer
        :items="model.data.template"
        :headers="model.headers"
      >
        <template v-slot:item.index="{ index }">
          {{ index + 1 }}
        </template>

        <template v-slot:item.pin="{ index }">
          <VSelect hide-details v-model="model.data.template[index].pin" :items="pinNumbers" />
        </template>

        <template v-slot:item.event="{ index }">
          <VSelect
            hide-details
            v-model="model.data.template[index].event"
            item-value="id"
            item-title="event_type"
            :items="events"
          />
        </template>

        <template v-slot:item.action="{ item }">
          <VBtn icon color="error" density="compact" @click="model.deleteRow(item.pin)">
            <VIcons type="delete" />
          </VBtn>
        </template>
      </VDataTable>
    </VForm>

    <div class="form__btn_between mt-4">
      <VBtn @click="addHandler">{{ $t('itf.add_row') }}</VBtn>

      <div class="form__btn_right">
        <VBtn :loading="isPending" :disabled="!valid" @click="submitHandler">
          {{ $t('itf.save') }}
        </VBtn>

        <VBtn color="default" @click="model.openForm = false">{{ $t('itf.close') }}</VBtn>
      </div>
    </div>
  </VModal>
</template>
